import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { biometricService, BiometricStatus } from '../../services/biometric.service';
import { Capacitor } from '@capacitor/core';
import { isNativeApp } from '../../utils/platform.utils';
import { uploadQueueService } from '../../services/upload-queue.service';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { backgroundGeolocationService } from '../../services/background-geolocation.service';

interface HeaderProps {
  onlineStatus: boolean;
}

// Geofence event for history
interface GeofenceEvent {
  identifier: string;
  action: 'ENTER' | 'EXIT' | 'DWELL';
  timestamp: string;
}

// Geofence detail for debug display
interface GeofenceDetail {
  identifier: string;
  radius: number;
  latitude: number;
  longitude: number;
  notifyOnEntry: boolean;
  notifyOnExit: boolean;
  notifyOnDwell: boolean;
}

// Debug info interface
interface DebugInfo {
  bgGeo: {
    authStatus: string;
    isMoving: boolean;
    enabled: boolean;
    trackingMode: number;
    didLaunchInBackground: boolean;
    distanceFilter: number;
    desiredAccuracy: number;
    geofenceProximityRadius: number;
    geofenceInitialTriggerEntry: boolean;
    error?: string;
  } | null;
  deviceInfo: {
    platform: string;
    model: string;
    manufacturer: string;
    version: string;
    framework: string;
  } | null;
  licenseInfo: {
    os: string;
    app_id: string;
    order_number: number;
    product: string;
    max_build_stamp: number;
    entitlements: string[];
    valid: boolean;
    error?: string;
  } | null;
  trackingState: {
    isTracking: boolean;
    currentJobId: number | null;
    currentEntryId: number | null;
    employeeId: number | null;
  };
  currentLocation: {
    lat: number;
    lng: number;
    accuracy: number;
    timestamp: string;
  } | null;
  geofences: {
    current: string[]; // Currently registered geofence identifiers
    details: GeofenceDetail[]; // Full geofence details with radius
    insideOf: string[]; // Geofences user is currently inside
    history: GeofenceEvent[]; // Recent enter/exit events
  };
  queue: {
    pending: number;
    uploading: number;
    failed: number;
    total: number;
  };
  pendingNative: string[];
  pendingFinalize: string[];
  uploadDebugLogs: string[];
}

export const Header: React.FC<HeaderProps> = ({ onlineStatus }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState<BiometricStatus | null>(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // Main pages show hamburger, sub-pages show back button
  const mainPages = ['/', '/login', '/dashboard'];
  const isMainPage = mainPages.includes(location.pathname);

  const translations = {
    en: {
      online: 'Online',
      offline: 'Offline',
      profile: 'Profile',
      timeRecords: 'Time Records',
      uploadStatus: 'Upload Status',
      installApp: 'Install App',
      updateApp: 'Check for Updates',
      logout: 'Logout',
      biometricEnabled: 'Enabled',
      biometricDisabled: 'Disabled',
      enableBiometric: 'Enable',
      disableBiometric: 'Disable'
    },
    es: {
      online: 'En línea',
      offline: 'Sin conexión',
      profile: 'Perfil',
      timeRecords: 'Registros de Tiempo',
      uploadStatus: 'Estado de Carga',
      installApp: 'Instalar Aplicación',
      updateApp: 'Buscar Actualizaciones',
      logout: 'Cerrar Sesión',
      biometricEnabled: 'Habilitado',
      biometricDisabled: 'Deshabilitado',
      enableBiometric: 'Habilitar',
      disableBiometric: 'Deshabilitar'
    }
  };

  const t = translations[language];

  const handleLogout = () => {
    auth?.logout();
    navigate('/login');
  };

  useEffect(() => {
    // Check if app is already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as any).standalone ||
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check biometric status
    const checkBiometrics = async () => {
      const status = await biometricService.getStatus();
      setBiometricStatus(status);
    };
    checkBiometrics();
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);

    // Dispatch custom event to notify all components
    window.dispatchEvent(new CustomEvent('languageChange', { detail: newLang }));
  };

  const handleInstallClick = () => {
    console.log('Install App button clicked');
    // Trigger install prompt by dispatching custom event
    const event = new CustomEvent('showInstallPrompt');
    console.log('Dispatching showInstallPrompt event:', event);
    window.dispatchEvent(event);
    setMenuOpen(false);
  };

  const handleBiometricToggle = async () => {
    if (!biometricStatus) return;

    if (biometricStatus.isEnabled) {
      // Disable biometrics
      await biometricService.disable();
      const status = await biometricService.getStatus();
      setBiometricStatus(status);
    } else {
      // Redirect to login to enable biometrics (needs PIN to store credentials)
      setMenuOpen(false);
      // Clear current session to force re-login with biometric setup
      auth?.logout();
      navigate('/login');
    }
  };

  // Fetch debug info for native debug modal
  const fetchDebugInfo = async () => {
    setDebugLoading(true);
    try {
      // Get BackgroundGeolocation state
      let bgGeoInfo: DebugInfo['bgGeo'] = null;
      let deviceInfoData: DebugInfo['deviceInfo'] = null;
      let licenseInfoData: DebugInfo['licenseInfo'] = null;
      let geofenceInfo: DebugInfo['geofences'] = {
        current: [],
        details: [],
        insideOf: [],
        history: [],
      };
      let currentLocation: DebugInfo['currentLocation'] = null;

      try {
        const state = await BackgroundGeolocation.getState();
        const providerState = await BackgroundGeolocation.getProviderState();

        // Map authorization status to readable string
        const authStatusMap: { [key: number]: string } = {
          [BackgroundGeolocation.AUTHORIZATION_STATUS_NOT_DETERMINED]: 'NOT_DETERMINED',
          [BackgroundGeolocation.AUTHORIZATION_STATUS_RESTRICTED]: 'RESTRICTED',
          [BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED]: 'DENIED',
          [BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS]: 'ALWAYS',
          [BackgroundGeolocation.AUTHORIZATION_STATUS_WHEN_IN_USE]: 'WHEN_IN_USE',
        };

        bgGeoInfo = {
          authStatus: authStatusMap[providerState.status] || `UNKNOWN(${providerState.status})`,
          isMoving: state.isMoving || false,
          enabled: state.enabled || false,
          trackingMode: state.trackingMode || 0,
          didLaunchInBackground: state.didLaunchInBackground || false,
          distanceFilter: state.distanceFilter || 0,
          desiredAccuracy: state.desiredAccuracy || 0,
          geofenceProximityRadius: state.geofenceProximityRadius || 0,
          geofenceInitialTriggerEntry: state.geofenceInitialTriggerEntry ?? false,
        };

        // Get registered geofences with full details
        try {
          const geofences = await BackgroundGeolocation.getGeofences();
          geofenceInfo.current = geofences.map(g => g.identifier);
          geofenceInfo.details = geofences.map(g => ({
            identifier: g.identifier,
            radius: g.radius,
            latitude: g.latitude,
            longitude: g.longitude,
            notifyOnEntry: g.notifyOnEntry ?? true,
            notifyOnExit: g.notifyOnExit ?? true,
            notifyOnDwell: g.notifyOnDwell ?? false,
          }));
        } catch (geoErr) {
          console.log('Could not get geofences:', geoErr);
        }

        // Get current location
        try {
          const loc = await BackgroundGeolocation.getCurrentPosition({ samples: 1, timeout: 10 });
          currentLocation = {
            lat: loc.coords.latitude,
            lng: loc.coords.longitude,
            accuracy: loc.coords.accuracy,
            timestamp: new Date(loc.timestamp).toLocaleTimeString(),
          };
        } catch (locErr) {
          console.log('Could not get current position:', locErr);
        }

        // Get geofence history from localStorage
        try {
          const historyJson = localStorage.getItem('debug_geofence_history');
          if (historyJson) {
            geofenceInfo.history = JSON.parse(historyJson).slice(-30);
          }
        } catch (histErr) {
          console.log('Could not get geofence history:', histErr);
        }

        // Determine which geofences user is currently inside
        // Based on history - last ENTER without matching EXIT
        const insideSet = new Set<string>();
        for (const event of geofenceInfo.history) {
          if (event.action === 'ENTER') {
            insideSet.add(event.identifier);
          } else if (event.action === 'EXIT') {
            insideSet.delete(event.identifier);
          }
        }
        geofenceInfo.insideOf = Array.from(insideSet);

        // Get device info
        try {
          const di = await BackgroundGeolocation.getDeviceInfo();
          deviceInfoData = {
            platform: di.platform,
            model: di.model,
            manufacturer: di.manufacturer,
            version: di.version,
            framework: di.framework,
          };

          // Decode license JWT to show license status
          // Pick the correct license key based on platform
          const isIOS = di.platform === 'iOS';
          const licenseKey = isIOS
            ? (state as any).license || ''
            : ''; // Android license is in AndroidManifest.xml, not accessible from JS

          // Try to decode the JWT payload (base64url encoded)
          // For Android, we'll read from a known location
          const rawKey = isIOS
            ? licenseKey
            : 'eyJhbGciOiJFZERTQSIsImtpZCI6ImVkMjU1MTktbWFpbi12MSJ9.eyJvcyI6ImFuZHJvaWQiLCJhcHBfaWQiOiJjb20uYnJhZGFubmEuZmllbGQiLCJvcmRlcl9udW1iZXIiOjE1MzM3LCJyZW5ld2FsX3VybCI6Imh0dHBzOi8vc2hvcC50cmFuc2lzdG9yc29mdC5jb20vY2FydC8zOTM2NzA3MTIzNjE5OToxP25vdGU9MTAyMTAiLCJjdXN0b21lcl9pZCI6OTI4OSwicHJvZHVjdCI6ImNhcGFjaXRvci1iYWNrZ3JvdW5kLWdlb2xvY2F0aW9uIiwia2V5X3ZlcnNpb24iOjEsImFsbG93ZWRfc3VmZml4ZXMiOlsiLmRldiIsIi5kZXZlbG9wbWVudCIsIi5zdGFnaW5nIiwiLnN0YWdlIiwiLnFhIiwiLnVhdCIsIi50ZXN0IiwiLmRlYnVnIl0sIm1heF9idWlsZF9zdGFtcCI6MjAyNzAyMjUsImdyYWNlX2J1aWxkcyI6MCwiZW50aXRsZW1lbnRzIjpbImNvcmUiXSwiaWF0IjoxNzY5MzQ4MzYyfQ.A9A6q1A72MNY5BvbGhy9MaE_c_jN2x2DBwPjx4bVMZN7_Hx_M4Tw7iGjE1BUf9oIMShEJ6lZcuUxMWASeaBuBg';

          try {
            const parts = rawKey.split('.');
            if (parts.length >= 2) {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              const now = parseInt(new Date().toISOString().slice(0, 10).replace(/-/g, ''));
              licenseInfoData = {
                os: payload.os || 'unknown',
                app_id: payload.app_id || 'unknown',
                order_number: payload.order_number || 0,
                product: payload.product || 'unknown',
                max_build_stamp: payload.max_build_stamp || 0,
                entitlements: payload.entitlements || [],
                valid: payload.max_build_stamp >= now,
              };
            }
          } catch (jwtErr) {
            licenseInfoData = {
              os: isIOS ? 'ios' : 'android',
              app_id: 'com.bradanna.field',
              order_number: 0,
              product: 'unknown',
              max_build_stamp: 0,
              entitlements: [],
              valid: false,
              error: 'Failed to decode license JWT',
            };
          }
        } catch (devErr) {
          console.log('Could not get device info:', devErr);
        }

      } catch (bgErr: any) {
        bgGeoInfo = {
          authStatus: 'ERROR',
          isMoving: false,
          enabled: false,
          trackingMode: 0,
          didLaunchInBackground: false,
          distanceFilter: 0,
          desiredAccuracy: 0,
          geofenceProximityRadius: 0,
          geofenceInitialTriggerEntry: false,
          error: bgErr?.message || String(bgErr),
        };
      }

      // Get tracking state from background service
      const trackingState = backgroundGeolocationService.getState();

      // Get upload queue stats
      const queueStats = await uploadQueueService.getQueueStats();

      // Get pending native uploads from localStorage
      const pendingNative: string[] = [];
      const pendingFinalize: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('pending_native_upload_')) {
          pendingNative.push(key);
        }
        if (key?.startsWith('pending_finalize_')) {
          pendingFinalize.push(key);
        }
      }

      // Get global upload debug logs
      const uploadDebugLogs = ((window as any).__uploadDebug || []).slice(-20) as string[];

      setDebugInfo({
        bgGeo: bgGeoInfo,
        deviceInfo: deviceInfoData,
        licenseInfo: licenseInfoData,
        trackingState,
        currentLocation,
        geofences: geofenceInfo,
        queue: queueStats,
        pendingNative,
        pendingFinalize,
        uploadDebugLogs,
      });
    } catch (err: any) {
      console.error('Error fetching debug info:', err);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleDebugClick = () => {
    setMenuOpen(false);
    setShowDebugModal(true);
    fetchDebugInfo();
  };

  const handleUpdateClick = async () => {
    setMenuOpen(false);

    // Show loading message
    const loadingDiv = document.createElement('div');
    loadingDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      padding: 30px 40px;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      z-index: 10000;
      text-align: center;
      font-size: 18px;
      color: #1f2937;
      font-weight: 600;
    `;
    loadingDiv.textContent = 'Updating app...';
    document.body.appendChild(loadingDiv);

    // Clear all caches
    if ('serviceWorker' in navigator && 'caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map(key => caches.delete(key)));

      // Unregister all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }

    // Force hard reload
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <>
      <header className="header">
        <div className="header-content">
          {isMainPage ? (
            <button
              className="menu-button"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              ☰
            </button>
          ) : (
            <button
              className="menu-button back-button"
              onClick={() => navigate(-1)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <h1 className="header-title">Bradanna</h1>
          <div className="header-actions">
            <button onClick={toggleLanguage} className="language-toggle">
              {language === 'en' ? 'ES' : 'EN'}
            </button>
            <div className={`status-indicator ${onlineStatus ? 'online' : 'offline'}`}>
              {onlineStatus ? `● ${t.online}` : `○ ${t.offline}`}
            </div>
          </div>
        </div>
      </header>

      <div className={`slide-menu ${menuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          <h2>{auth?.user?.first_name} {auth?.user?.last_name}</h2>
          <button
            className="close-menu"
            onClick={() => setMenuOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="menu-items">
          <button onClick={() => { navigate('/profile'); setMenuOpen(false); }}>
            👤 {t.profile}
          </button>
          <button onClick={() => { navigate('/time'); setMenuOpen(false); }}>
            ⏰ {t.timeRecords}
          </button>
          <button onClick={() => { navigate('/upload-status'); setMenuOpen(false); }}>
            📤 {t.uploadStatus}
          </button>
          {biometricStatus?.isAvailable && (
            <button onClick={handleBiometricToggle} className="biometric-toggle-button">
              <span>
                {biometricStatus.biometryName === 'Face ID' ? '👤' : '👆'} {biometricStatus.biometryName}
              </span>
              <span className={`biometric-status ${biometricStatus.isEnabled ? 'enabled' : 'disabled'}`}>
                {biometricStatus.isEnabled ? t.biometricEnabled : t.biometricDisabled}
              </span>
            </button>
          )}
          {!isStandalone && !Capacitor.isNativePlatform() && (
            <button onClick={handleInstallClick}>
              📲 {t.installApp}
            </button>
          )}
          {!Capacitor.isNativePlatform() && (
            <button onClick={handleUpdateClick}>
              🔄 {t.updateApp}
            </button>
          )}
          <a
            href="/privacy-policy.html"
            className="menu-link"
            onClick={() => setMenuOpen(false)}
          >
            🔒 {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
          </a>
          {isNativeApp() && (
            <button onClick={handleDebugClick}>
              🐛 Debug
            </button>
          )}
          <hr />
          <button onClick={handleLogout} className="logout-button">
            🚪 {t.logout}
          </button>
        </div>
      </div>

      {/* Debug Modal - Native only */}
      {showDebugModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowDebugModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '20px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '18px' }}>🐛 Debug Info</h2>
              <button
                onClick={() => setShowDebugModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            {debugLoading ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Loading...</div>
            ) : debugInfo ? (
              <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                {/* Tracking State Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#f59e0b', marginBottom: '8px', fontSize: '14px' }}>⏱ Tracking State</h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>Active: <span style={{ color: debugInfo.trackingState.isTracking ? '#4ade80' : '#f87171', fontWeight: '600' }}>{debugInfo.trackingState.isTracking ? 'YES' : 'NO'}</span></div>
                    <div>Current Job: <span style={{ color: '#94a3b8' }}>{debugInfo.trackingState.currentJobId ?? 'None'}</span></div>
                    <div>Entry ID: <span style={{ color: '#94a3b8' }}>{debugInfo.trackingState.currentEntryId ?? 'None'}</span></div>
                    <div>Employee ID: <span style={{ color: '#94a3b8' }}>{debugInfo.trackingState.employeeId ?? 'None'}</span></div>
                  </div>
                </div>

                {/* Device Info */}
                {debugInfo.deviceInfo && (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#8b5cf6', marginBottom: '8px', fontSize: '14px' }}>📱 Device</h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>Platform: <span style={{ color: '#94a3b8' }}>{debugInfo.deviceInfo.platform}</span></div>
                    <div>Model: <span style={{ color: '#94a3b8' }}>{debugInfo.deviceInfo.manufacturer} {debugInfo.deviceInfo.model}</span></div>
                    <div>OS: <span style={{ color: '#94a3b8' }}>{debugInfo.deviceInfo.version}</span></div>
                  </div>
                </div>
                )}

                {/* License Info */}
                {debugInfo.licenseInfo && (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: debugInfo.licenseInfo.valid ? '#4ade80' : '#f87171', marginBottom: '8px', fontSize: '14px' }}>
                    🔑 License {debugInfo.licenseInfo.valid ? '(Valid)' : '(EXPIRED)'}
                  </h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>OS: <span style={{ color: '#94a3b8' }}>{debugInfo.licenseInfo.os}</span></div>
                    <div>App ID: <span style={{ color: '#94a3b8' }}>{debugInfo.licenseInfo.app_id}</span></div>
                    <div>Order: <span style={{ color: '#94a3b8' }}>#{debugInfo.licenseInfo.order_number}</span></div>
                    <div>Expires: <span style={{ color: debugInfo.licenseInfo.valid ? '#4ade80' : '#f87171' }}>
                      {String(debugInfo.licenseInfo.max_build_stamp).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                    </span></div>
                    <div>Entitlements: <span style={{ color: '#94a3b8' }}>{debugInfo.licenseInfo.entitlements.join(', ')}</span></div>
                    {debugInfo.licenseInfo.error && (
                      <div style={{ color: '#f87171' }}>Error: {debugInfo.licenseInfo.error}</div>
                    )}
                  </div>
                </div>
                )}

                {/* BackgroundGeolocation Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>📍 Plugin State</h3>
                  {debugInfo.bgGeo?.error ? (
                    <div style={{ color: '#f87171' }}>Error: {debugInfo.bgGeo.error}</div>
                  ) : (
                    <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                      <div>Auth: <span style={{ color: debugInfo.bgGeo?.authStatus === 'ALWAYS' ? '#4ade80' : debugInfo.bgGeo?.authStatus === 'WHEN_IN_USE' ? '#fbbf24' : '#f87171' }}>{debugInfo.bgGeo?.authStatus}</span></div>
                      <div>Enabled: <span style={{ color: debugInfo.bgGeo?.enabled ? '#4ade80' : '#f87171' }}>{debugInfo.bgGeo?.enabled ? 'YES' : 'NO'}</span></div>
                      <div>Mode: <span style={{ color: '#94a3b8' }}>{debugInfo.bgGeo?.trackingMode === 0 ? 'Geofence-Only' : 'Location + Geofence'}</span></div>
                      <div>Proximity Radius: <span style={{ color: '#94a3b8' }}>{debugInfo.bgGeo?.geofenceProximityRadius}m</span></div>
                      <div>Initial Trigger: <span style={{ color: debugInfo.bgGeo?.geofenceInitialTriggerEntry ? '#4ade80' : '#f87171' }}>{debugInfo.bgGeo?.geofenceInitialTriggerEntry ? 'YES' : 'NO'}</span></div>
                      <div>Launch in BG: {debugInfo.bgGeo?.didLaunchInBackground ? 'YES' : 'NO'}</div>
                    </div>
                  )}
                </div>

                {/* Current Location */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>📌 Current Location</h3>
                  {debugInfo.currentLocation ? (
                    <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                      <div>Lat: <span style={{ color: '#94a3b8' }}>{debugInfo.currentLocation.lat.toFixed(6)}</span></div>
                      <div>Lng: <span style={{ color: '#94a3b8' }}>{debugInfo.currentLocation.lng.toFixed(6)}</span></div>
                      <div>Accuracy: <span style={{ color: debugInfo.currentLocation.accuracy < 50 ? '#4ade80' : debugInfo.currentLocation.accuracy < 200 ? '#fbbf24' : '#f87171' }}>{Math.round(debugInfo.currentLocation.accuracy)}m</span></div>
                      <div>Time: <span style={{ color: '#94a3b8' }}>{debugInfo.currentLocation.timestamp}</span></div>
                    </div>
                  ) : (
                    <div style={{ color: '#f87171' }}>Could not get location</div>
                  )}
                </div>

                {/* Geofence Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>🔲 Geofences ({debugInfo.geofences?.details?.length || 0})</h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>Currently Inside: <span style={{
                      color: (debugInfo.geofences?.insideOf?.length || 0) > 0 ? '#4ade80' : '#f87171',
                      fontWeight: '600'
                    }}>
                      {(debugInfo.geofences?.insideOf?.length || 0) > 0
                        ? debugInfo.geofences.insideOf.join(', ')
                        : 'NONE'}
                    </span></div>
                  </div>

                  {/* Registered geofences list */}
                  {(debugInfo.geofences?.details?.length || 0) > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '4px' }}>Registered geofences:</div>
                      <div style={{
                        backgroundColor: '#0f172a',
                        borderRadius: '6px',
                        padding: '8px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                      }}>
                        {debugInfo.geofences.details.map((g, i) => (
                          <div
                            key={i}
                            style={{
                              color: '#e2e8f0',
                              borderBottom: '1px solid #334155',
                              paddingBottom: '4px',
                              marginBottom: '4px',
                              fontSize: '10px',
                            }}
                          >
                            <div style={{ fontWeight: '600' }}>{g.identifier}</div>
                            <div style={{ color: '#94a3b8' }}>
                              r={g.radius}m | {g.latitude.toFixed(4)},{g.longitude.toFixed(4)} | E:{g.notifyOnEntry ? '✓' : '✗'} X:{g.notifyOnExit ? '✓' : '✗'} D:{g.notifyOnDwell ? '✓' : '✗'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geofence History */}
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '4px' }}>Event history (last 30):</div>
                    <div style={{
                      backgroundColor: '#0f172a',
                      borderRadius: '6px',
                      padding: '8px',
                      maxHeight: '150px',
                      overflowY: 'auto',
                    }}>
                      {(!debugInfo.geofences?.history || debugInfo.geofences.history.length === 0) ? (
                        <div style={{ color: '#64748b', fontSize: '11px' }}>No geofence events yet</div>
                      ) : (
                        [...debugInfo.geofences.history].reverse().map((event, i) => (
                          <div
                            key={i}
                            style={{
                              color: event.action === 'ENTER' ? '#4ade80' : event.action === 'EXIT' ? '#f87171' : '#fbbf24',
                              borderBottom: '1px solid #334155',
                              paddingBottom: '2px',
                              marginBottom: '2px',
                              fontSize: '10px',
                            }}
                          >
                            {event.action === 'ENTER' ? '→ ENTER' : event.action === 'EXIT' ? '← EXIT' : '◉ DWELL'} {event.identifier} @ {event.timestamp}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Queue Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>📤 Upload Queue (IndexedDB)</h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>Pending: <span style={{ color: debugInfo.queue.pending > 0 ? '#fbbf24' : '#4ade80' }}>{debugInfo.queue.pending}</span></div>
                    <div>Uploading: <span style={{ color: debugInfo.queue.uploading > 0 ? '#3b82f6' : '#e2e8f0' }}>{debugInfo.queue.uploading}</span></div>
                    <div>Failed: <span style={{ color: debugInfo.queue.failed > 0 ? '#f87171' : '#e2e8f0' }}>{debugInfo.queue.failed}</span></div>
                    <div>Total: {debugInfo.queue.total}</div>
                  </div>
                </div>

                {/* Native Pending Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>📱 Native Pending (localStorage)</h3>
                  <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                    <div>Native Uploads: <span style={{ color: debugInfo.pendingNative.length > 0 ? '#fbbf24' : '#4ade80' }}>{debugInfo.pendingNative.length}</span></div>
                    <div>Pending Finalize: <span style={{ color: debugInfo.pendingFinalize.length > 0 ? '#fbbf24' : '#4ade80' }}>{debugInfo.pendingFinalize.length}</span></div>
                  </div>
                </div>

                {/* Upload Debug Logs */}
                <div>
                  <h3 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '14px' }}>📝 Upload Debug Logs ({debugInfo.uploadDebugLogs.length})</h3>
                  <div style={{
                    backgroundColor: '#0f172a',
                    borderRadius: '6px',
                    padding: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}>
                    {debugInfo.uploadDebugLogs.length === 0 ? (
                      <div style={{ color: '#64748b' }}>No logs yet</div>
                    ) : (
                      debugInfo.uploadDebugLogs.map((log, i) => (
                        <div
                          key={i}
                          style={{
                            color: log.includes('ERROR') || log.includes('FAILED') ? '#f87171' : log.includes('SUCCESS') ? '#4ade80' : '#94a3b8',
                            borderBottom: '1px solid #334155',
                            paddingBottom: '4px',
                            marginBottom: '4px',
                            wordBreak: 'break-all',
                          }}
                        >
                          {log}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={fetchDebugInfo}
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  🔄 Refresh
                </button>

                {/* Clear Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={() => {
                      (window as any).__uploadDebug = [];
                      fetchDebugInfo();
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#475569',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    🗑️ Clear Upload Logs
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('debug_geofence_history');
                      fetchDebugInfo();
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#475569',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    🗑️ Clear Geofence History
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ color: '#94a3b8', textAlign: 'center' }}>No debug info</div>
            )}
          </div>
        </div>
      )}

      {menuOpen && (
        <div
          className="menu-overlay"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
};
